import { useCallback, useEffect, useState } from "react";

import type { SxProps } from "@mui/material/styles";

import { useAppDispatch } from "app/hooks";
import { useAppSelector } from "app/hooks";
import { TagTypes } from "constants/tags";
import { selectCurrentUser } from "features/auth/auth-slice";
import {
  useAddAssignedTagMutation,
  useRemoveAssignedTagMutation
} from "services/assigned-tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { StaffTask } from "services/types/staff-tasks.type";
import { Tag } from "services/types/tags.type";

import {
  setCustomMessage,
  setOpen
} from "../action-snackbar/action-snackbar-slice";
import AssignedTagsList from "./index";

type AssignedTaskTagsProps = {
  task: StaffTask;
  readonly?: boolean;
  onChangeTag?: () => void;
  sx?: SxProps;
};

const AssignedTaskTags = ({
  task,
  readonly,
  onChangeTag,
  sx
}: AssignedTaskTagsProps) => {
  const dispatch = useAppDispatch();

  const [assignedTags, setAssignedTags] = useState(task?.assigned_tags || []);
  const currentUser = useAppSelector(selectCurrentUser);

  useEffect(() => {
    setAssignedTags(task?.assigned_tags || []);
  }, [task?.assigned_tags]);

  const [addTag] = useAddAssignedTagMutation();
  const [removeTag] = useRemoveAssignedTagMutation();

  const handleUpdate = useCallback(
    (tag: AssignedTag) => {
      setAssignedTags((prev) =>
        prev.map((assignedTag) =>
          assignedTag.id === tag.id ? tag : assignedTag
        )
      );
      onChangeTag?.();
    },
    [onChangeTag]
  );

  const handleAdd = (tag_id: number, tag: Tag) => {
    addTag({
      tag: tag_id,
      tag_type: TagTypes.TASK,
      object_id: task.id
    }).then((response) => {
      if ("data" in response) {
        setAssignedTags((prev) => [
          ...prev,
          {
            ...response.data,
            tag,
            created_by: currentUser as any,
            updated_by: currentUser as any
          }
        ]);
        onChangeTag?.();
        dispatch(setCustomMessage("Tag added"));
        dispatch(setOpen(1500));
      } else if ("error" in response) {
        dispatch(setCustomMessage("Failed to add tag"));
        dispatch(setOpen(1500));
      }
    });
  };

  const handleDelete = (tag: number) => {
    removeTag({ tag, tag_type: TagTypes.TASK }).then((response) => {
      if ("data" in response) {
        setAssignedTags((prev) => prev.filter((prevTag) => prevTag.id !== tag));
        onChangeTag?.();
        dispatch(setCustomMessage("Tag removed"));
        dispatch(setOpen(1500));
      } else if ("error" in response) {
        dispatch(setCustomMessage("Failed to remove tag"));
        dispatch(setOpen(1500));
      }
    });
  };

  if (!task?.id) {
    return null;
  }

  return (
    <AssignedTagsList
      tagType={TagTypes.TASK}
      tags={assignedTags}
      onAdd={readonly ? undefined : handleAdd}
      onDelete={readonly ? undefined : handleDelete}
      onUpdate={handleUpdate}
      sx={{ flexDirection: "column", ...sx }}
    />
  );
};

export default AssignedTaskTags;
